/**
 * Export.
 *
 * Two things come out of one document. The manuscript is what gets read from
 * the pulpit, and slide blocks are stripped from it — see `lib/slides.ts` for
 * why they live inline in the first place. The deck is what goes on the screen,
 * and it only appears in this menu when the document actually contains slides.
 * A sermon with no slide block sees exactly the menu it saw before the feature
 * existed.
 */

import { useState } from "react";
import { Download, FileDown, FileText, Images, Presentation, Projector } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/contexts/ThemeContext";
import { parseSlides } from "@/lib/slides";
import { runDocumentExport } from "@/lib/document-export";

interface ExportMenuProps {
  title: string;
  /** Lazy: serialising the document is O(n), and only export needs it. */
  content: string | (() => string);
  /** Render actions inside a parent dropdown submenu instead of a trigger. */
  itemsOnly?: boolean;
}

export function ExportMenu({ title, content, itemsOnly = false }: ExportMenuProps) {
  const getContent = () => (typeof content === "function" ? content() : content);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  // Whatever the author picked in Settings → Slides — palette and frame shape
  // together. Every deck export uses it.
  const { slideStyle } = useTheme();

  // Read at render so the slide items appear as soon as a document has slides.
  // One regex scan of the source, and only while this menu is mounted.
  const { slides } = parseSlides(getContent());

  /**
   * Run an export, reporting either way.
   *
   * Every action here can fail for reasons the author can act on — a blocked
   * pop-up, a canvas that would not encode — so none of them fail silently.
   */
  const run = async (label: string, action: () => void | Promise<void>) => {
    setIsExporting(true);
    try {
      await action();
      toast({ title: `${label} exported` });
    } catch (error) {
      console.error(`Export failed: ${label}`, error);
      toast({
        title: `Could not export ${label.toLowerCase()}`,
        description: error instanceof Error ? error.message : "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportKind = (
    label: string,
    kind: "manuscript-md" | "manuscript-print" | "source-md" | "pptx" | "images" | "slides-pdf" | "marp",
  ) =>
    run(label, () =>
      runDocumentExport({
        kind,
        title,
        content: getContent(),
        slideStyle,
      }),
    );

  const hasSlides = slides.length > 0;

  const items = (
    <>
      {hasSlides && <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Manuscript</DropdownMenuLabel>}
      <DropdownMenuItem
        onClick={() => exportKind("Manuscript markdown", "manuscript-md")}
        disabled={isExporting}
      >
        <FileText className="h-4 w-4 mr-2" />
        <span>Markdown</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => exportKind("Manuscript PDF", "manuscript-print")} disabled={isExporting}>
        <Download className="h-4 w-4 mr-2" />
        <span>PDF / Print</span>
      </DropdownMenuItem>

      {hasSlides && (
        <>
          {/* Only reachable when stripping actually loses something, so the
              ordinary case never has to choose between two markdown exports. */}
          <DropdownMenuItem
            onClick={() => exportKind("Full source", "source-md")}
            disabled={isExporting}
          >
            <FileText className="h-4 w-4 mr-2" />
            <span>Full source, slides included</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {slides.length} {slides.length === 1 ? "slide" : "slides"} · {slideStyle.aspect.id}
          </DropdownMenuLabel>

          <DropdownMenuItem
            onClick={() => exportKind("PowerPoint", "pptx")}
            disabled={isExporting}
          >
            <Presentation className="h-4 w-4 mr-2" />
            <span>PowerPoint</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => exportKind("Slide images", "images")}
            disabled={isExporting}
          >
            <Images className="h-4 w-4 mr-2" />
            <span>Images, zipped</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => exportKind("Slides PDF", "slides-pdf")}
            disabled={isExporting}
          >
            <Projector className="h-4 w-4 mr-2" />
            <span>Slides PDF</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => exportKind("Marp markdown", "marp")}
            disabled={isExporting}
          >
            <FileDown className="h-4 w-4 mr-2" />
            <span>Marp markdown</span>
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  if (itemsOnly) return items;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 text-muted-foreground"
          disabled={isExporting}
        >
          <FileDown className="h-4 w-4" />
          <span className="hidden md:inline">Export</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{items}</DropdownMenuContent>
    </DropdownMenu>
  );
}
